// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ActionButton, ErrorCause, Feedback, Frame, User } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';

// utils
import { translateError } from '../../../utils/translations';

// props
export interface DialogUpdateUserStatusContentProps {
	alert?: string;
	btnSubmit: ActionButton;
	i18nKey: string;
	title: string;
}

export interface DialogUpdateUserStatusProps {
	frame?: Frame;
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
	user: User;
}

export const DialogUpdateUserStatus = ({
	frame,
	isOpen,
	onClose,
	onSuccess,
	user,
}: DialogUpdateUserStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogStatus = searchParams.get('dialogStatus');

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// state
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);

	// vars
	const isActivating = paramDialogStatus === 'ACTIVE';
	const isDeactivating = paramDialogStatus === 'DEACTIVATED';
	const isInviting = paramDialogStatus === 'INVITED';

	// deactivate translations
	const dialog: DialogUpdateUserStatusContentProps = {
		btnSubmit: {
			children: t('deactivateUser'),
			color: 'error',
		},
		i18nKey: 'dialogUpdateUserStatus.deactivate.content',
		title: t('deactivateUser'),
	};

	if (isDeactivating) {
		dialog.alert = t('alert.deactivateUser');
	}

	// invite translations
	if (isInviting) {
		dialog.btnSubmit = {
			children: t('resendInvite'),
			color: 'primary',
		};
		dialog.i18nKey = 'dialogUpdateUserStatus.invite.content';
		dialog.title = t('resendInvite');
	}

	// activate translations
	// use activate translations for supplier and beneficiary users even though they will be reinvited
	if (isActivating || (isInviting && frame !== 'ENTITY')) {
		dialog.btnSubmit = {
			children: t('activateUser'),
			color: 'primary',
		};
		dialog.alert = t('alert.activateUser');
		dialog.i18nKey = 'dialogUpdateUserStatus.activate.content';
		dialog.title = t('activateUser');
	}

	// patch user
	const { fetchRequest: updateUser, isLoading: isLoadingUpdateUser } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/users/${user.id}`,
		options: {
			method: 'PATCH',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
			body: JSON.stringify({
				projects: [
					{
						id: project?.id,
						status: paramDialogStatus,
					},
				],
			}),
		},
		onError: (error) => {
			const err = error as Error;
			const cause = err.cause as ErrorCause;
			const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
				items: [],
			};

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});

			setFeedback(feedbackNew);
		},
		onSuccess: () => {
			let message = t('alert.userDeactivated', { name: user.fullName });

			if (isActivating) {
				message = t('alert.userActivated', { name: user.fullName });
			}

			if (isInviting) {
				message = t('alert.userInvited', { name: user.fullName });
			}

			// set banner
			setBanner({
				hasClose: true,
				message,
				severity: 'success',
			});

			if (onSuccess) {
				onSuccess();
			}

			// close dialog
			if (onClose) {
				onClose();
			}
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// update user status
		updateUser();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					...dialog.btnSubmit,
					loading: isLoadingUpdateUser,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'user',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			subtitle={{
				children: user.fullName,
			}}
			title={{
				children: dialog.title,
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.orange.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.i18nKey}
						values={{ name: user.fullName }}
					/>
				</Grid>
				{dialog.alert && (
					<Grid item xs={12}>
						<Alert severity="warning">{dialog.alert}</Alert>
					</Grid>
				)}
				{feedback?.items?.length ? (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				) : null}
			</Grid>
		</DialogActions>
	);
};
